// Standard status types that all custom statuses map to
export enum TaskStatusTypeEnum {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  ON_HOLD = 'ON_HOLD',
  CANCELED = 'CANCELED',
}

// Friendly display names for status types
export const STATUS_DISPLAY_NAMES: Record<TaskStatusTypeEnum, string> = {
  [TaskStatusTypeEnum.NOT_STARTED]: 'To Do',
  [TaskStatusTypeEnum.IN_PROGRESS]: 'In Progress',
  [TaskStatusTypeEnum.COMPLETED]: 'Done',
  [TaskStatusTypeEnum.ON_HOLD]: 'On Hold',
  [TaskStatusTypeEnum.CANCELED]: 'Canceled',
};

// Default colors for status types
export const STATUS_COLORS: Record<TaskStatusTypeEnum, string> = {
  [TaskStatusTypeEnum.NOT_STARTED]: '#E5E7EB', // Gray
  [TaskStatusTypeEnum.IN_PROGRESS]: '#60A5FA', // Blue
  [TaskStatusTypeEnum.COMPLETED]: '#34D399', // Green
  [TaskStatusTypeEnum.ON_HOLD]: '#FBBF24', // Yellow
  [TaskStatusTypeEnum.CANCELED]: '#EF4444', // Red
};

// Default descriptions for status types
export const STATUS_DESCRIPTIONS: Record<TaskStatusTypeEnum, string> = {
  [TaskStatusTypeEnum.NOT_STARTED]: 'Tasks that have not been started yet',
  [TaskStatusTypeEnum.IN_PROGRESS]: 'Tasks that are currently being worked on',
  [TaskStatusTypeEnum.COMPLETED]: 'Tasks that have been completed',
  [TaskStatusTypeEnum.ON_HOLD]: 'Tasks that are temporarily on hold',
  [TaskStatusTypeEnum.CANCELED]: 'Tasks that have been canceled',
};

// Default order for status types
export const STATUS_ORDER: Record<TaskStatusTypeEnum, number> = {
  [TaskStatusTypeEnum.NOT_STARTED]: 1,
  [TaskStatusTypeEnum.IN_PROGRESS]: 2,
  [TaskStatusTypeEnum.COMPLETED]: 3,
  [TaskStatusTypeEnum.ON_HOLD]: 4,
  [TaskStatusTypeEnum.CANCELED]: 5,
};

// Helper function to get status type info
export function getStatusTypeInfo(statusType: TaskStatusTypeEnum) {
  return {
    name: STATUS_DISPLAY_NAMES[statusType],
    color: STATUS_COLORS[statusType],
    description: STATUS_DESCRIPTIONS[statusType],
    order: STATUS_ORDER[statusType],
  };
}

// Helper function to get all status types
export function getAllStatusTypes() {
  return Object.values(TaskStatusTypeEnum).map(statusType => ({
    id: statusType,
    name: STATUS_DISPLAY_NAMES[statusType],
    color: STATUS_COLORS[statusType],
    description: STATUS_DESCRIPTIONS[statusType],
    order: STATUS_ORDER[statusType],
  }));
}

// ============================================
// Status Migration Types
// ============================================

/**
 * Represents how a single old status maps to a new status during migration
 */
export interface StatusMigrationMapping {
  /** ID of the status being removed/changed */
  oldStatusId: string;
  /** Display name of the old status */
  oldStatusName: string;
  /** Color of the old status (for UI display) */
  oldStatusColor: string;
  /** ID of the new status to migrate to (null if no auto-match found) */
  newStatusId: string | null;
  /** Display name of the new status (null if no auto-match found) */
  newStatusName: string | null;
  /** How the match was determined */
  matchType: 'exact' | 'name' | 'type' | 'none';
  /** Number of tasks currently using this old status */
  taskCount: number;
}

/**
 * Preview of what will happen during a status migration
 */
export interface StatusMigrationPreview {
  /** All status mappings (including auto-matched and orphaned) */
  mappings: StatusMigrationMapping[];
  /** Status IDs that have no auto-match and require user input */
  orphanedStatuses: string[];
  /** Total number of tasks that will be affected by the migration */
  totalAffectedTasks: number;
}

/**
 * User-provided custom mapping for a status
 */
export interface StatusCustomMapping {
  /** ID of the old status to migrate from */
  oldStatusId: string;
  /** ID of the new status to migrate to */
  newStatusId: string;
} 